"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEmailComposerAction } from "../analytics";
import { useEmailComposer } from "../context";
import { normalizeImageHref } from "../utils";
import { useMutation } from "@tanstack/react-query";
import { NodeSelection } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuAlignCenter, LuAlignLeft, LuAlignRight, LuLink, LuLink2Off, LuLoader, LuPencil, LuSparkles } from "react-icons/lu";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cx } from "@/lib/tailwind-utils";
import { getClickedCellIndex, getClickedNonTextBlockIndex, getClickedNonTextBlockType } from "./node-selection-drag-plugin";
const WIDTH_OPTIONS = ["25%", "50%", "75%", "100%"] as const;
const ALIGNMENT_OPTIONS = [{
  value: "left",
  icon: LuAlignLeft,
  label: "Left"
}, {
  value: "center",
  icon: LuAlignCenter,
  label: "Center"
}, {
  value: "right",
  icon: LuAlignRight,
  label: "Right"
}] as const;
function Separator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-px h-4 bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Find the emailImage node from a row-level NodeSelection.
 * Searches the clicked cell first, then falls back to all cells.
 */
function getImageFromSelection(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
editor: any): {
  attrs: Record<string, unknown>;
  pos: number;
} | null {
  if (!editor) return null;
  const sel = editor.state.selection;
  if (!(sel instanceof NodeSelection)) return null;

  // Direct NodeSelection on an emailImage (simple mode)
  if (sel.node.type.name === "emailImage") {
    return {
      attrs: sel.node.attrs,
      pos: sel.from
    };
  }
  if (sel.node.type.name !== "emailRow") return null;
  const row = sel.node;
  const clickedIdx = getClickedCellIndex();
  function searchCell(cellIdx: number, blockIndex: number): {
    attrs: Record<string, unknown>;
    pos: number;
  } | null {
    if (cellIdx >= row.childCount) return null;
    let cellStart = sel.from + 1;
    for (let c = 0; c < cellIdx; c++) cellStart += row.child(c).nodeSize;
    const cell = row.child(cellIdx);
    let offset = cellStart + 1;
    let matchCount = 0;
    for (let i = 0; i < cell.childCount; i++) {
      const block = cell.child(i);
      if (block.type.name === "emailImage") {
        if (matchCount === blockIndex) {
          return {
            attrs: block.attrs,
            pos: offset
          };
        }
        matchCount++;
      }
      offset += block.nodeSize;
    }
    return null;
  }

  // Only search the clicked cell, targeting the specific block index
  const blockIndex = getClickedNonTextBlockIndex();
  const result = searchCell(clickedIdx, blockIndex);
  if (!result) return null;

  // When the cell has multiple non-text blocks (e.g. image + button),
  // only show this menu if the user actually clicked the image.
  const clickedType = getClickedNonTextBlockType();
  if (clickedType && clickedType !== "emailImage") return null;
  return result;
}
export function ImageBubbleMenu() {
  const {
    editor,
    bubbleMenuContainerRef,
    onGenerateAltText
  } = useEmailComposer();
  const [altOpen, setAltOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const altInputRef = useRef<HTMLInputElement>(null);
  const state = useEditorState({
    editor,
    selector: ctx => {
      const result = getImageFromSelection(ctx.editor);
      if (!result) return null;
      const {
        attrs,
        pos
      } = result;
      return {
        isActive: !!(attrs.src as string),
        width: attrs.width as string ?? "100%",
        alignment: attrs.alignment as string ?? "center",
        alt: attrs.alt as string ?? "",
        src: attrs.src as string ?? "",
        href: attrs.href as string ?? "",
        imagePos: pos
      };
    }
  });
  const selectedImageHref = state?.href ?? "";
  const selectedImagePos = state?.imagePos;
  const updateAttr = useCallback((attrs_0: Record<string, unknown>) => {
    if (!editor || !state) return;
    const imageNode = editor.state.doc.nodeAt(state.imagePos);
    if (!imageNode || imageNode.type.name !== "emailImage") return;
    const sel = editor.state.selection;
    const {
      tr
    } = editor.state;
    tr.setNodeMarkup(state.imagePos, undefined, {
      ...imageNode.attrs,
      ...attrs_0
    });
    if (sel instanceof NodeSelection) {
      tr.setSelection(NodeSelection.create(tr.doc, sel.from));
    }
    editor.view.dispatch(tr);
  }, [editor, state]);
  useEffect(() => {
    if (!linkOpen || selectedImagePos == null) return;
    setLinkDraft(selectedImageHref);
  }, [linkOpen, selectedImageHref, selectedImagePos]);
  const openLinkEditor = useCallback(() => {
    if (!state) return;
    setLinkDraft(state.href);
    setLinkOpen(true);
  }, [state]);
  const applyLink = useCallback(() => {
    const href = normalizeImageHref(linkDraft);
    updateAttr({
      href
    });
    setLinkDraft(href);
    setLinkOpen(false);
    trackEmailComposerAction("image_link_applied", "bubble_menu");
  }, [linkDraft, updateAttr]);
  const removeLink = useCallback(() => {
    updateAttr({
      href: ""
    });
    setLinkDraft("");
    setLinkOpen(false);
    trackEmailComposerAction("image_link_removed", "bubble_menu");
  }, [updateAttr]);
  const {
    mutate: handleGenerateAlt,
    isPending: isGenerating
  } = useMutation({
    mutationFn: async () => {
      if (!onGenerateAltText || !state?.src) return;
      const altText = await onGenerateAltText(state.src);
      if (altText) {
        updateAttr({
          alt: altText
        });
        trackEmailComposerAction("image_alt_generated", "bubble_menu");
      }
    }
  });
  if (!editor || !state?.isActive) return null;
  const hasRenderableLink = !!normalizeImageHref(selectedImageHref);
  return <BubbleMenu editor={editor} shouldShow={() => getImageFromSelection(editor) !== null} getReferencedVirtualElement={() => {
    const result_0 = getImageFromSelection(editor);
    if (!result_0) return null;
    const sel_0 = editor.state.selection;
    if (!(sel_0 instanceof NodeSelection)) return null;

    // Direct NodeSelection on emailImage (simple mode)
    if (sel_0.node.type.name === "emailImage") {
      const dom = editor.view.nodeDOM(sel_0.from);
      const img = dom instanceof HTMLImageElement ? dom : dom instanceof HTMLElement ? dom.querySelector("img") : null;
      if (!img) return null;
      return {
        getBoundingClientRect: () => img.getBoundingClientRect(),
        getClientRects: () => [img.getBoundingClientRect()],
        contextElement: img
      };
    }

    // Row-level selection (block mode) — find the clicked cell and <img>
    const rowDom = editor.view.nodeDOM(sel_0.from);
    if (!(rowDom instanceof HTMLElement)) return null;
    const clickedIdx = getClickedCellIndex();
    const cellDom = rowDom.children[clickedIdx] ?? rowDom.children[0];
    if (!(cellDom instanceof HTMLElement)) return null;
    const imgs = cellDom.querySelectorAll("img");
    const imgIdx = getClickedNonTextBlockIndex();
    const img_0 = imgs[imgIdx] ?? imgs[0];
    if (!img_0) return null;
    return {
      getBoundingClientRect: () => img_0.getBoundingClientRect(),
      getClientRects: () => [img_0.getBoundingClientRect()],
      contextElement: img_0
    };
  }} options={{
    autoPlacement: {
      allowedPlacements: ["top", "bottom"],
      crossAxis: true
    },
    inline: true
  }} appendTo={bubbleMenuContainerRef.current ?? undefined}>
      <div className="rounded-lg border border-border bg-background shadow-md px-1.5 py-1 flex items-center gap-1">
        {/* Width */}
        <span className="text-caption text-foreground-secondary px-1">
          Width
        </span>
        {WIDTH_OPTIONS.map(w => <Button type="button" key={w} size="sm" variant="ghost" className={cx("h-6 px-1.5 text-caption", state.width === w ? "bg-hover text-foreground font-semibold" : "text-foreground-secondary")} onMouseDown={e => e.preventDefault()} onClick={() => {
        updateAttr({
          width: w
        });
        trackEmailComposerAction("image_width_changed", "bubble_menu", {
          width: w
        });
      }}>
            {w}
          </Button>)}

        <Separator />

        {/* Alignment */}
        {ALIGNMENT_OPTIONS.map(({
        value,
        icon: Icon,
        label
      }) => <Button type="button" key={value} size="icon-sm" variant="ghost" className={cx("h-6 w-6", state.alignment === value && "bg-hover")} onMouseDown={e_0 => e_0.preventDefault()} onClick={() => {
        updateAttr({
          alignment: value
        });
        trackEmailComposerAction("image_alignment_changed", "bubble_menu", {
          alignment: value
        });
      }} tooltip={label}>
            <Icon size={14} />
          </Button>)}

        <Separator />

        {/* Link */}
        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="ghost" className={cx("h-6 px-1.5 text-caption gap-1", hasRenderableLink ? "text-foreground" : "text-foreground-secondary")} onMouseDown={e_1 => e_1.preventDefault()} onClick={openLinkEditor} tooltip="Image link">
              <LuLink size={12} />
              Link
            </Button>
          </PopoverTrigger>
          <PopoverContent align="center" sideOffset={8} className="w-80 p-2 space-y-2">
            <Input value={linkDraft} onChange={e_2 => setLinkDraft(e_2.target.value)} onKeyDown={e_3 => {
            if (e_3.key === "Enter") applyLink();
          }} placeholder="https://example.com" className="h-7 text-caption" />
            <div className="flex items-center justify-between gap-2">
              <Button type="button" size="sm" variant="ghost" className="h-6 gap-1 text-caption text-foreground-secondary" onMouseDown={e_4 => e_4.preventDefault()} onClick={removeLink} disabled={!state.href && !linkDraft}>
                <LuLink2Off size={12} />
                Remove
              </Button>
              <Button variant="secondary" type="button" size="sm" className="h-6 text-caption" onMouseDown={e_5 => e_5.preventDefault()} onClick={applyLink}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Separator />

        {/* Alt text */}
        <Popover open={altOpen} onOpenChange={setAltOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="ghost" className={cx("h-6 px-1.5 text-caption gap-1", state.alt ? "text-foreground" : "text-foreground-secondary")} onMouseDown={e_6 => e_6.preventDefault()} tooltip="Alt text">
              <LuPencil size={12} />
              Alt
            </Button>
          </PopoverTrigger>
          <PopoverContent align="center" sideOffset={8} className="w-72 p-2 space-y-2" onOpenAutoFocus={() => {
          setTimeout(() => altInputRef.current?.focus(), 0);
        }}>
            <Input ref={altInputRef} value={state.alt} onChange={e_7 => updateAttr({
            alt: e_7.target.value
          })} placeholder="Describe this image..." className="h-7 text-caption" />
            {onGenerateAltText && <Button type="button" size="sm" variant="ghost" className="h-6 w-full justify-center gap-1 text-caption text-foreground-secondary" onMouseDown={e_8 => e_8.preventDefault()} onClick={() => handleGenerateAlt()} disabled={isGenerating || !state.src}>
                {isGenerating ? <>
                    <LuLoader size={12} className="animate-spin" />
                    Analyzing image…
                  </> : <>
                    <LuSparkles size={12} />
                    {state.alt ? "Regenerate with AI" : "Generate with AI"}
                  </>}
              </Button>}
          </PopoverContent>
        </Popover>
      </div>
    </BubbleMenu>;
}